import { Box, TextInput, Group, Button } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconSearch, IconX, IconCalendar } from '@tabler/icons-react';

import { useGetPayments } from '../api/getPayments';
import { usePaymentsSearch } from '../hooks/usePaymentsSearch';

import { EmptyState } from './EmptyState';
import { PaymentsTable } from './Table';

import { ListLoader } from '@/components';
import { parsePaymentDate } from '@/utils/dates';

export const PaymentsList = () => {
  const { data, isLoading } = useGetPayments();
  const {
    currencySearch,
    setCurrencySearch,
    clientSearch,
    setClientSearch,
    paymentMethodSearch,
    setPaymentMethodSearch,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    clearCurrencySearch,
    clearClientSearch,
    clearPaymentMethodSearch,
    clearDateRange,
  } = usePaymentsSearch();

  if (isLoading) {
    return <ListLoader height="calc(100vh - 70px - 32px)" />;
  }

  if (!data?.payments.length) {
    return <EmptyState />;
  }

  // Filter payments to check search results
  const filteredPayments = data.payments.filter((payment) => {
    const matchesCurrency = payment.currency.toLowerCase().includes(currencySearch.toLowerCase());
    const matchesClient = payment.client_details.toLowerCase().includes(clientSearch.toLowerCase());
    const matchesPaymentMethod = payment.payment_method
      .toLowerCase()
      .includes(paymentMethodSearch.toLowerCase());

    // Check date range
    const paymentDate = parsePaymentDate(payment.date);
    if (!paymentDate) return false;

    const paymentDateOnly = new Date(paymentDate);
    paymentDateOnly.setHours(0, 0, 0, 0);

    const dateFromOnly = dateFrom
      ? (() => {
          const d = new Date(dateFrom);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const dateToOnly = dateTo
      ? (() => {
          const d = new Date(dateTo);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const matchesDateFrom = !dateFromOnly || paymentDateOnly >= dateFromOnly;
    const matchesDateTo = !dateToOnly || paymentDateOnly <= dateToOnly;

    return (
      matchesCurrency && matchesClient && matchesPaymentMethod && matchesDateFrom && matchesDateTo
    );
  });

  const hasActiveSearch =
    currencySearch || clientSearch || paymentMethodSearch || dateFrom || dateTo;

  return (
    <Box
      style={{
        height: 'calc(100vh - 70px - 32px)', // 70px header height + 32px padding (16px top + 16px bottom)
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box mb="md" style={{ flexShrink: 0 }}>
        <Group>
          <TextInput
            placeholder="Search by currency..."
            value={currencySearch}
            onChange={(event) => setCurrencySearch(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            rightSection={
              currencySearch && (
                <Button
                  variant="subtle"
                  size="xs"
                  p={0}
                  onClick={clearCurrencySearch}
                  style={{ minWidth: 'auto', width: 'auto', height: 'auto' }}
                >
                  <IconX size={14} />
                </Button>
              )
            }
            style={{ flex: 1, maxWidth: 300 }}
          />
          <TextInput
            placeholder="Search by client..."
            value={clientSearch}
            onChange={(event) => setClientSearch(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            rightSection={
              clientSearch && (
                <Button
                  variant="subtle"
                  size="xs"
                  p={0}
                  onClick={clearClientSearch}
                  style={{ minWidth: 'auto', width: 'auto', height: 'auto' }}
                >
                  <IconX size={14} />
                </Button>
              )
            }
            style={{ flex: 1, maxWidth: 300 }}
          />
          <TextInput
            placeholder="Search by payment method..."
            value={paymentMethodSearch}
            onChange={(event) => setPaymentMethodSearch(event.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            rightSection={
              paymentMethodSearch && (
                <Button
                  variant="subtle"
                  size="xs"
                  p={0}
                  onClick={clearPaymentMethodSearch}
                  style={{ minWidth: 'auto', width: 'auto', height: 'auto' }}
                >
                  <IconX size={14} />
                </Button>
              )
            }
            style={{ flex: 1, maxWidth: 300 }}
          />
        </Group>
        <Group mt="md">
          <DatePickerInput
            placeholder="Date from..."
            value={dateFrom}
            onChange={setDateFrom}
            leftSection={<IconCalendar size={16} />}
            rightSection={
              dateFrom && (
                <Button
                  variant="subtle"
                  size="xs"
                  p={0}
                  onClick={() => setDateFrom(null)}
                  style={{ minWidth: 'auto', width: 'auto', height: 'auto' }}
                >
                  <IconX size={14} />
                </Button>
              )
            }
            style={{ flex: 1, maxWidth: 200 }}
          />
          <DatePickerInput
            placeholder="Date to..."
            value={dateTo}
            onChange={setDateTo}
            minDate={dateFrom || undefined}
            leftSection={<IconCalendar size={16} />}
            rightSection={
              dateTo && (
                <Button
                  variant="subtle"
                  size="xs"
                  p={0}
                  onClick={() => setDateTo(null)}
                  style={{ minWidth: 'auto', width: 'auto', height: 'auto' }}
                >
                  <IconX size={14} />
                </Button>
              )
            }
            style={{ flex: 1, maxWidth: 200 }}
          />
          {(dateFrom || dateTo) && (
            <Button
              variant="subtle"
              size="sm"
              onClick={clearDateRange}
              leftSection={<IconX size={14} />}
            >
              Clear dates
            </Button>
          )}
        </Group>
      </Box>
      <Box style={{ flex: 1, minHeight: 0 }}>
        {filteredPayments.length === 0 && hasActiveSearch ? (
          <NoSearchResults />
        ) : (
          <PaymentsTable
            payments={data.payments}
            currencySearch={currencySearch}
            clientSearch={clientSearch}
            paymentMethodSearch={paymentMethodSearch}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}
      </Box>
    </Box>
  );
};

function NoSearchResults() {
  return (
    <Box
      style={{
        padding: '20px',
        textAlign: 'left',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
      }}
    >
      No search results found
    </Box>
  );
}
